import React, { type Dispatch, type ReactNode, type SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { Spacing8 } from 'autogen/design-tokens/tokens';
import type {
    ClickAndCollectBasketStoreResult,
    DeliveryParcelShopOption,
    StoreParcelShopResult
} from 'autogen/swagger/all';
import { OpeningHours as OpeningHoursText } from 'autogen/translation-keys/trans-website-storeopeninghours';
import CardWithTag from 'components/CardWithTag';
import { InlineButtonWithIcon } from 'components/generic/InlineButton';
import { Body, Typography } from 'components/generic/typography';
import Icon from 'components/Icon';
import Radio from 'components/shared/inputs/radio';
import format from 'helpers/translations';
import { capitalizeFirstLetter } from 'modules/helpers/strings';
import StatusRenderer from './components/StatusRenderer';
import { getTagStyling } from './shared/helpers';

export type StoreCardProps = {
    model: DeliveryParcelShopOption | StoreParcelShopResult | ClickAndCollectBasketStoreResult;
    setSelectedId: Dispatch<SetStateAction<string | number | undefined>>;
    setSelectedItem?: Dispatch<
        SetStateAction<DeliveryParcelShopOption | StoreParcelShopResult | ClickAndCollectBasketStoreResult | undefined>
    >;
    nameType: 'store' | 'parcelShop';
    statusTag?: string;
    showUnavailableStatus?: boolean;
    loading?: boolean;
    selected?: boolean;
    disabled?: boolean;
    additionalContentRenderer?: () => ReactNode;
    additionalContentCollapsedRenderer?: () => ReactNode;
    priceRenderer?: () => ReactNode;
    supplierRenderer?: () => ReactNode;
    showAdditionalInfoCollapsed?: boolean;
    onClick?: () => void;
};

const StyledCardWithTag = styled(CardWithTag)<{ $loading?: boolean }>`
    opacity: ${({ $loading }) => ($loading ? 0.4 : 1)};
    transition: opacity 300ms ease;
`;

const GridContainer = styled.div<{ $selected?: boolean; $additionalContentCollapsed?: boolean }>`
    display: grid;
    grid-gap: ${({ theme }) => theme.Spacing16};
    grid-template-columns: min-content 1fr max-content;
    width: 100%;
    min-width: 0;

        ${({ $selected, $additionalContentCollapsed }) =>
            ($selected &&
                css`
                        grid-template-areas:
                            'radio content additionalInfo'
                            '. content price';
                    `) ||
            (!$selected &&
                $additionalContentCollapsed &&
                css`
                        grid-template-areas:
                            'radio content additionalInfo'
                            '. content .';
                    `) ||
            (
                !$selected &&
                    css`
                        grid-template-areas:
                            'radio content additionalInfo';
                    `
            )}
`;

const StyledRadio = styled(Radio)`
    grid-area: radio;
`;

const Content = styled.div`
    grid-area: content;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.Spacing16};
    min-width: 0;
`;

const ContentCollapsed = styled.div`
    grid-area: content;
    display: flex;
    flex-direction: column;
    gap: ${Spacing8};
    min-width: 0;
`;

const Address = styled.div`
    & > :first-child {
        font-weight: ${({ theme }) => theme.TypographyBodyWeightStrong};
    }

    & > :not(:first-child) {
        color: ${({ theme }) => theme.ColorTextSecondary};
    }
`;

const StyledBody = styled(Typography.Body)`
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
`;

const StyledInlineButton = styled(InlineButtonWithIcon)`
    margin-right: auto;
    text-decoration: none;
`;

const OpeningHours = styled.div`
    display: grid;
    grid-template-columns: max-content 1fr;
    grid-column-gap: ${({ theme }) => theme.Spacing12};
    color: ${({ theme }) => theme.ColorTextSecondary};
`;

const AdditionalInfo = styled.div`
    grid-area: additionalInfo;
    display: flex;
    flex-direction: column;
    align-items: end;
    gap: ${({ theme }) => theme.Spacing8};
`;

const AdditionalInfoCollapsed = styled.div`
    grid-area: additionalInfo;
    display: flex;
    justify-content: end;
    align-items: center;
`;

const Distance = styled(Body)`
    align-self: end;
    text-align: right;
`;

function StoreCard(props: StoreCardProps) {
    const {
        model,
        selected,
        disabled,
        setSelectedId,
        setSelectedItem,
        additionalContentRenderer,
        additionalContentCollapsedRenderer,
        priceRenderer,
        supplierRenderer,
        nameType,
        statusTag,
        showUnavailableStatus,
        loading,
        showAdditionalInfoCollapsed = true,
        ...restProps
    } = props;
    const { addressLines, distance, openingHours, tags, parcelShopId, clickAndCollectStoreStatus } = model;
    const [productStockStatus, setProductStockStatus] = useState<StoreParcelShopResult['productStockStatus']>();
    const [collapsedOpeningHours, setCollapsedOpeningHours] = useState(true);
    const [primaryTag, secondaryTag] = tags || [];
    const { t } = useTranslation();

    function getNameAndAddress() {
        return {
            name: model.name,
            address: `<div>${addressLines[0]},</div> <div>${addressLines[1]}</div>`
        };
    }

    const tagArray = [
        primaryTag && {
            backgroundColor: getTagStyling(primaryTag.type),
            text: primaryTag.text
        },
        secondaryTag && {
            backgroundColor: getTagStyling(secondaryTag.type),
            text: secondaryTag.text
        }
    ].filter(Boolean);

    const tagsProp = tagArray.length > 0 ? tagArray : undefined;

    function isStoreParcelShopResult(
        model: DeliveryParcelShopOption | StoreParcelShopResult | ClickAndCollectBasketStoreResult
    ): model is StoreParcelShopResult {
        return (model as StoreParcelShopResult).productStockStatus !== undefined;
    }

    function handleOnChange(): void {
        setSelectedId(parcelShopId);
        setSelectedItem?.(model);
    }

    useEffect(() => {
        if (selected) {
            handleOnChange();
        }
    }, [selected, handleOnChange]);

    useEffect(() => {
        if (isStoreParcelShopResult(model)) {
            setProductStockStatus(model.productStockStatus);
        }
    }, []);

    return (
        <StyledCardWithTag
            tags={selected ? tagsProp : undefined}
            forwardedAs="li"
            cardAs="label"
            disabled={disabled}
            $loading={loading}
            {...restProps}
        >
            <GridContainer $selected={selected} $additionalContentCollapsed={!!additionalContentCollapsedRenderer}>
                <StyledRadio readOnly checked={selected} onChange={handleOnChange} disabled={disabled} />
                {selected ? (
                    <>
                        <Content>
                            {getNameAndAddress() && (
                                <Address>
                                    <Body as="div" size="L" strong>
                                        {format(getNameAndAddress()?.name)}
                                    </Body>
                                    <Body as="div" size="M">
                                        {format(getNameAndAddress()?.address)}
                                    </Body>
                                </Address>
                            )}
                            <StyledInlineButton
                                fontWeight="regular"
                                icon={
                                    collapsedOpeningHours ? (
                                        <Icon type="ChevronDown" size={16} />
                                    ) : (
                                        <Icon type="ChevronUp" size={16} />
                                    )
                                }
                                iconProperties={{ position: 'right' }}
                                onClick={() => setCollapsedOpeningHours((prevState) => !prevState)}
                            >
                                {t(OpeningHoursText)}
                            </StyledInlineButton>
                            {!collapsedOpeningHours && (
                                <OpeningHours>
                                    {openingHours.map(
                                        (open): JSX.Element => (
                                            <React.Fragment key={open.days}>
                                                <Body as="span">{capitalizeFirstLetter(open.days)}:</Body>
                                                <Body as="span">{open.openingHours}</Body>
                                            </React.Fragment>
                                        )
                                    )}
                                </OpeningHours>
                            )}
                            {additionalContentRenderer?.()}
                        </Content>
                        <AdditionalInfo>
                            <Distance forwardedAs="span" size="M" strong>
                                {distance}
                            </Distance>
                            {supplierRenderer?.()}
                        </AdditionalInfo>
                        {priceRenderer?.()}
                    </>
                ) : (
                    <>
                        <ContentCollapsed>
                            {getNameAndAddress() && (
                                <StyledBody forwardedAs="div" size="L">
                                    {format(getNameAndAddress()?.name)}
                                </StyledBody>
                            )}
                            {additionalContentCollapsedRenderer?.()}
                        </ContentCollapsed>
                        {showAdditionalInfoCollapsed && (
                            <AdditionalInfoCollapsed>
                                {nameType === 'parcelShop' ? (
                                    supplierRenderer?.()
                                ) : (
                                    <StatusRenderer
                                        status={productStockStatus || clickAndCollectStoreStatus}
                                        statusTag={statusTag}
                                        showUnavailableStatus={showUnavailableStatus}
                                    />
                                )}
                            </AdditionalInfoCollapsed>
                        )}
                    </>
                )}
            </GridContainer>
        </StyledCardWithTag>
    );
}

export default StoreCard;
